import React from 'react'
import { motion } from 'framer-motion'
import { Upload, Trash2, CheckCircle } from 'lucide-react'
import { Event } from '../types'

const AdminImportPage = () => {
  const [csvText, setCsvText] = React.useState('')
  const [imported, setImported] = React.useState<Event[]>([])
  const [message, setMessage] = React.useState<string | null>(null)

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem('importedEvents')
      if (raw) setImported(JSON.parse(raw))
    } catch {}
  }, [])

  const save = (events: Event[]) => {
    localStorage.setItem('importedEvents', JSON.stringify(events))
    setImported(events)
  }

  const clearAll = () => {
    localStorage.removeItem('importedEvents')
    setImported([])
    setMessage('Cleared imported events')
    setTimeout(() => setMessage(null), 2000)
  }

  const parseCSV = (text: string): Event[] => {
    const lines = text.split(/\r?\n/).filter(Boolean)
    if (lines.length === 0) return []
    const header = splitCSVLine(lines[0]).map((h) => h.trim().toLowerCase())
    const idx = (name: string) => header.indexOf(name)
    const out: Event[] = []
    for (let i = 1; i < lines.length; i++) {
      const cols = splitCSVLine(lines[i])
      if (cols.length === 0) continue
      const get = (name: string) => {
        const j = idx(name)
        return j >= 0 ? cols[j]?.replace(/^\"|\"$/g, '') : ''
      }
      const genres = get('genres') ? get('genres').split('|').map((g) => g.trim()).filter(Boolean) : []
      const lat = parseFloat(get('lat'))
      const lng = parseFloat(get('lng'))
      const ev: Event = {
        id: get('id') || `${Date.now()}-${i}`,
        title: get('title') || 'Untitled',
        description: get('description') || '',
        artist: get('artist') || '',
        venue: get('venue') || '',
        startsAt: get('startsat') || new Date().toISOString(),
        price: get('price') || 'Free',
        genres,
        image: get('image') || 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
        attendees: Number(get('attendees')) || 0,
        rating: Number(get('rating')) || 0,
        reviews: Number(get('reviews')) || 0,
        status: (get('status') as any) || 'upcoming',
        location: {
          address: get('address') || '',
          coordinates: [isFinite(lat) ? lat : 0, isFinite(lng) ? lng : 0]
        }
      }
      out.push(ev)
    }
    return out
  }

  const splitCSVLine = (line: string): string[] => {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++ } else { inQuotes = !inQuotes }
      } else if (ch === ',' && !inQuotes) {
        result.push(current)
        current = ''
      } else {
        current += ch
      }
    }
    result.push(current)
    return result
  }

  const handleImport = () => {
    const events = parseCSV(csvText)
    save(events)
    setMessage(`Imported ${events.length} events`)
    setTimeout(() => setMessage(null), 2500)
  }

  return (
    <div className="pt-16 lg:pt-20 min-h-screen">
      <section className="section-padding py-16">
        <div className="container-max max-w-4xl mx-auto">
          <motion.div className="text-center mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-4xl font-display font-bold text-white mb-3">Admin — Import Events</h1>
            <p className="text-white/70">Paste CSV with headers: id,title,description,artist,venue,startsAt,price,genres,image,address,lat,lng,attendees,rating,reviews,status</p>
          </motion.div>

          {message && (
            <div className="glass-card p-4 mb-4 text-green-300 flex items-center gap-2"><CheckCircle size={18} />{message}</div>
          )}

          <div className="glass-card p-6 mb-6">
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={10}
              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-le-accent"
              placeholder="id,title,description,artist,venue,startsAt,price,genres,image,address,lat,lng,attendees,rating,reviews,status\n1,Rock Show,Big riffs...,Thunder Road,Lava Tonic,2025-07-20T21:00:00Z,$30,Rock|Classic Rock,https://...,'1447 S Clifton Ave, Wichita, KS',37.6456,-97.2951,156,4.7,34,upcoming"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn-secondary inline-flex items-center gap-2" onClick={handleImport}><Upload size={18} />Import</button>
              <button className="btn-secondary inline-flex items-center gap-2" onClick={clearAll}><Trash2 size={18} />Clear</button>
            </div>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-semibold text-white">Imported Events</h2>
              <div className="text-white/60 text-sm">{imported.length} items</div>
            </div>
            {imported.length === 0 ? (
              <div className="text-white/60">No imported events yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="text-white/60">
                      <th className="px-2 py-1">Title</th>
                      <th className="px-2 py-1">Artist</th>
                      <th className="px-2 py-1">Venue</th>
                      <th className="px-2 py-1">Starts</th>
                      <th className="px-2 py-1">Genres</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imported.slice(0, 20).map((e) => (
                      <tr key={e.id} className="text-white/80 border-t border-white/10">
                        <td className="px-2 py-2">{e.title}</td>
                        <td className="px-2 py-2">{e.artist}</td>
                        <td className="px-2 py-2">{e.venue}</td>
                        <td className="px-2 py-2">{new Date(e.startsAt).toLocaleString()}</td>
                        <td className="px-2 py-2">{e.genres.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default AdminImportPage

